import React, { useState, useEffect, useRef } from 'react';
import { inviteMember, getCollabMembers, removeMember, updateMemberRole, searchUsers } from '../../services/api';
import { useToast } from '../../hooks/useToast';

const ROLE_COLORS = { owner: '#f59e0b', editor: '#10b981', viewer: '#6366f1' };

function UserSearch({ onSelect, disabled }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    if (val.length < 2) { setResults([]); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await searchUsers(val);
        setResults(data);
        setOpen(data.length > 0);
      } catch { setResults([]); }
    }, 300);
  };

  const pick = (user) => {
    onSelect(user);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        value={query}
        onChange={handleChange}
        placeholder="Search by email..."
        style={s.input}
        disabled={disabled}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onFocus={() => results.length > 0 && setOpen(true)}
      />
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.3)', marginTop: 4,
          overflow: 'hidden',
        }}>
          {results.map(u => (
            <div
              key={u.id}
              onMouseDown={() => pick(u)}
              style={{
                padding: '9px 12px', cursor: 'pointer', fontSize: 13,
                color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8,
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.1)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <span style={{
                width: 26, height: 26, borderRadius: '50%', background: '#6366f1',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, fontWeight: 700, color: '#fff', flexShrink: 0,
              }}>
                {u.email.slice(0, 2).toUpperCase()}
              </span>
              {u.email}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function InviteDialog({ projectId, myRole, onClose }) {
  const [selected, setSelected] = useState(null); // {id, email}
  const [role, setRole] = useState('editor');
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    getCollabMembers(projectId).then(setMembers).catch(() => {});
  }, [projectId]);

  const handleInvite = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      await inviteMember(projectId, selected.email, role);
      toast.success(`Invite sent to ${selected.email}`);
      setSelected(null);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (userId, email) => {
    if (!window.confirm(`Remove ${email} from this project?`)) return;
    try {
      await removeMember(projectId, userId);
      setMembers(prev => prev.filter(m => m.user_id !== userId));
      toast.success(`${email} removed`);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await updateMemberRole(projectId, userId, newRole);
      setMembers(prev => prev.map(m => m.user_id === userId ? { ...m, role: newRole } : m));
      toast.success('Role updated');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const isOwner = myRole === 'owner';

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <div style={s.header}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>👥 Collaborators</span>
          <button onClick={onClose} style={s.closeBtn}>✕</button>
        </div>

        {/* Invite section — owners only */}
        {isOwner && (
          <div style={{ marginBottom: 20 }}>
            <label style={s.label}>Invite a user</label>
            <UserSearch onSelect={setSelected} disabled={loading} />

            {selected && (
              <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ ...s.avatar, background: '#6366f1', flexShrink: 0 }}>
                  {selected.email.slice(0, 2).toUpperCase()}
                </div>
                <span style={{ flex: 1, fontSize: 13, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {selected.email}
                </span>
                <select value={role} onChange={e => setRole(e.target.value)} style={{ ...s.input, width: 100, padding: '5px 8px' }}>
                  <option value="editor">✏️ Editor</option>
                  <option value="viewer">👁 Viewer</option>
                </select>
                <button onClick={handleInvite} disabled={loading} style={s.primaryBtn}>
                  {loading ? '...' : 'Send Invite'}
                </button>
                <button onClick={() => setSelected(null)} style={s.closeBtn}>✕</button>
              </div>
            )}

            {/* Role legend */}
            <div style={{ marginTop: 10, display: 'flex', gap: 12 }}>
              <span style={{ fontSize: 11, color: '#10b981' }}>✏️ Editor — can view & edit files</span>
              <span style={{ fontSize: 11, color: '#6366f1' }}>👁 Viewer — read-only access</span>
            </div>
          </div>
        )}

        {/* Members list */}
        <label style={s.label}>Members ({members.length})</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 280, overflowY: 'auto' }}>
          {members.map(m => (
            <div key={m.user_id} style={s.memberRow}>
              <div style={{ ...s.avatar, background: ROLE_COLORS[m.role] || '#6366f1' }}>
                {(m.email || '?').slice(0, 2).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 13, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.email}</p>
              </div>
              {isOwner && m.role !== 'owner' ? (
                <select
                  value={m.role}
                  onChange={e => handleRoleChange(m.user_id, e.target.value)}
                  style={{ ...s.input, width: 90, padding: '3px 6px', fontSize: 11 }}
                >
                  <option value="editor">Editor</option>
                  <option value="viewer">Viewer</option>
                </select>
              ) : (
                <span style={{ fontSize: 11, fontWeight: 600, color: ROLE_COLORS[m.role], padding: '2px 8px', background: `${ROLE_COLORS[m.role]}20`, borderRadius: 10 }}>
                  {m.role}
                </span>
              )}
              {isOwner && m.role !== 'owner' && (
                <button onClick={() => handleRemove(m.user_id, m.email)} style={s.removeBtn} title="Remove">✕</button>
              )}
            </div>
          ))}
          {members.length === 0 && (
            <p style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: '16px 0' }}>
              No collaborators yet. Search and invite someone above.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

const s = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal: { background: 'var(--bg-card)', borderRadius: 12, padding: 24, width: '90%', maxWidth: 500, boxShadow: '0 20px 60px rgba(0,0,0,0.4)', border: '1px solid var(--border)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  closeBtn: { background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 16, cursor: 'pointer' },
  label: { display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' },
  input: { padding: '8px 10px', background: 'var(--bg-input, var(--bg-secondary))', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box', width: '100%' },
  primaryBtn: { padding: '7px 14px', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  memberRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'var(--bg-secondary)', borderRadius: 8 },
  avatar: { width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0 },
  removeBtn: { background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 13, padding: '2px 6px', borderRadius: 4 },
};
